'use client';

import {
  CheckCircleIcon,
  ChevronDownIcon,
  PhoneIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { neverMissACallFaqs } from '../../data/industryFaqs';
import { useAuth } from '../../contexts/AuthContextNext';
import { trackButtonClick, trackPageView, trackPhoneClick } from '../../lib/analytics';
import { getPlanDisplayPrice, PRICING_PLANS } from '../../types/pricing';

export default function Landing() {
  useEffect(() => {
    trackPageView('Never Miss a Call', undefined, { funnel_step: 'paid_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };
  const starterDisplayPrice = getPlanDisplayPrice(PRICING_PLANS[0], pricingContext);

  const features = [
    {
      title: "Never Miss a Call",
      description: "Answers 24/7, even when you're busy or closed"
    },
    {
      title: "Books Appointments",
      description: "Automatically schedules directly into your calendar"
    },
    {
      title: "Answers Questions",
      description: "Instantly responds with your business information"
    },
    {
      title: "Spam Protection",
      description: "Automatically blocks notifications from 10.5M+ spam numbers"
    },
    {
      title: "Setup in 5 Minutes",
      description: "Get your AI receptionist running today"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      business: "Sunshine Salon",
      initials: "SJ",
      rating: 5,
      text: "Greet has been a total game-changer for my salon. It handles all our booking calls perfectly, and I haven't missed a potential client since we started using it."
    },
    {
      name: "Mike Rodriguez",
      business: "Rodriguez Plumbing",
      initials: "MR", 
      rating: 5,
      text: "My customers get instant answers about pricing and availability, even at 2 AM. It's like having a professional receptionist who never sleeps."
    },
    {
      name: "Dr. Emily Chen",
      business: "Downtown Dental",
      initials: "EC",
      rating: 5,
      text: "The call transcripts are incredibly helpful-I can see exactly what patients are asking about and adjust our services accordingly."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal header - no navigation distractions */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold text-blue-600">Greet</div>
          <a 
            href="tel:+14158773166"
            onClick={() => trackPhoneClick('+14158773166', 'voicemail_lp_header')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
          >
            <PhoneIcon className="h-5 w-5" />
            (415) 877-3166
          </a>
        </div>
      </header>

      {/* Hero Section - Above the Fold */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Never Miss Another<br />
            <span className="text-blue-600">Customer Call</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
            AI receptionist answers 24/7, books appointments, and handles questions-while you focus on your business.
          </p>
          
          {/* Single Strong CTA */}
          <div className="mb-8">
            <Link 
              href={isAuthenticated ? "/app" : "/register"}
              onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "voicemail_lp_hero")}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-5 rounded-lg font-bold text-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Free 7-Day Trial
            </Link>
          </div>
          
          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600 mb-8">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span>Setup in 5 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span>No hidden fees</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span>Cancel anytime</span>
            </div>
          </div>

          {/* Price anchor */}
          <p className="text-lg text-gray-600">
            Starting at{' '}
            {starterDisplayPrice.showSale ? (
              <>
                <span className="font-semibold text-gray-400 line-through">{starterDisplayPrice.basePrice}</span>{' '}
                <span className="font-bold text-gray-900">{starterDisplayPrice.price}</span>
              </>
            ) : (
              <span className="font-bold text-gray-900">{starterDisplayPrice.price}</span>
            )}
            {' '}· Save $20,000+ vs hiring a receptionist
          </p>
        </div>
      </section>

      {/* Simple 4-Feature Grid */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4 p-6 rounded-xl bg-gray-50 hover:bg-blue-50 transition-colors">
                <CheckCircleIcon className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Trusted by Small Business Owners
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">{testimonial.initials}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-gray-600 text-sm">{testimonial.business}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple Pricing */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Affordable Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Much cheaper than hiring a receptionist
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {PRICING_PLANS.map((plan) => {
              const displayPrice = getPlanDisplayPrice(plan, pricingContext);
              const isPopular = plan.id === 'growth';

              return (
                <div
                  key={plan.id}
                  className={`${isPopular ? 'border-4 border-blue-600 bg-blue-50' : 'border-2 border-gray-200 hover:border-blue-500'} rounded-xl p-6 text-center relative transition-colors`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  {displayPrice.showSale && (
                    <div className="text-base font-semibold text-gray-400 line-through mb-1">
                      {displayPrice.basePrice.replace('/month', '')}
                    </div>
                  )}
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {displayPrice.price.replace('/month', '')}
                  </div>
                  <div className="text-gray-600 font-semibold mb-3">{plan.name}</div>
                  <div className="text-sm text-gray-500 mb-4">{plan.callsPerMonth} calls/month</div>
                  <div className="text-sm text-gray-600">
                    {plan.id === 'starter' ? 'Perfect for new businesses' : plan.id === 'growth' ? 'Includes appointment scheduling' : 'CRM integrations included'}
                  </div>
                  {displayPrice.showSale && displayPrice.label && (
                    <div className="mt-3 inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                      {displayPrice.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link 
              href={isAuthenticated ? "/app" : "/register"}
              onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "voicemail_lp_pricing")}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg font-bold text-lg transition-all"
            >
              Start Free 7-Day Trial
            </Link>
            <p className="text-sm text-gray-500 mt-3">All plans include 7-day free trial · No hidden fees</p>
          </div>
        </div>
      </section>

      {/* FAQ - Condensed */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Common Questions
          </h2>
          
          <div className="space-y-4">
            {neverMissACallFaqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 flex-shrink-0 ml-4 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div className={`px-6 text-gray-600 transition-all duration-300 ${
                  openFaq === index ? 'pb-4 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Missing Calls Today
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Setup takes 5 minutes. Start answering every call 24/7.
          </p>
          
          <Link 
            href={isAuthenticated ? "/app" : "/register"}
            onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "voicemail_lp_final")}
            className="inline-block bg-white text-blue-600 hover:bg-gray-50 px-10 py-5 rounded-lg font-bold text-xl transition-all transform hover:scale-105 shadow-lg"
          >
            Start Free 7-Day Trial
          </Link>
          
          <p className="text-blue-100 text-sm mt-6">
            No hidden fees · Cancel anytime
          </p>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm">
          <p>&copy; 2025 Greet. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
} 
